import { ChangeDetectorRef, Component, OnInit, Input, Output, EventEmitter, ViewChild, ViewChildren, QueryList, TemplateRef, inject, OnChanges, SimpleChanges, output } from '@angular/core';
import { Table } from 'primeng/table';
import { MultiSelect } from 'primeng/multiselect';
import { DatePipe } from '@angular/common';
import { ConfirmationService } from 'primeng/api';
import { UserPreferencesStore } from 'src/app/store/user-preferences/user-preferences.store';
import {
  DynamicColumnConfig,
  DynamicTableConfig,
  DynamicTableFilter,
  LazyLoadEvent,
  ExportEvent,
  SelectionEvent,
  FilterOption
} from './dynamic-table.model';
import { AuthStore } from 'src/app/store/authorization/auth.store';
import { Access, Function, Screen } from 'src/Utilities/enum';
import { SHARED_IMPORTS } from '../shared.module';

@Component({
  selector: 'app-dynamic-table',
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.css'],
  standalone: true,
  imports: [...SHARED_IMPORTS]
})

export class DynamicTableComponent implements OnInit, OnChanges {
  /** Data array to display in table */
  @Input() dataSource: any[] = [];

  /** Column configuration array */
  @Input() columns: DynamicColumnConfig[] = [];

  /** Table configuration object */
  @Input() config: DynamicTableConfig = {
    rows: 50,
    selectable: true,
    selectionMode: 'multiple',
    globalSearch: true,
    exportable: true,
    deletable: true,
    columnFilteringEnabled: true,
    lazy: false,
    dataKey: 'id',
    title: '' // Add title property
  };

  /** Loading state for table */
  @Input() loading: boolean = false;

  /** Function to determine if a row selection should be disabled */
  @Input() disableSelection: (row: any) => boolean = () => false;

  /** Custom cell templates map (field name -> TemplateRef) */
  @Input() cellTemplates: { [field: string]: TemplateRef<any> } = {};

  /** Total records count (for lazy loading pagination) */
  @Input() totalRecords: number = 0;

  // ========== OUTPUTS ==========

  /** Emitted when lazy loading is triggered (pagination, sort, filter) */
  @Output() lazyLoad = new EventEmitter<LazyLoadEvent>();

  /** Emitted when filters change */
  @Output() filterChange = new EventEmitter<DynamicTableFilter>();

  /** Emitted when row selection changes */
  @Output() rowsSelected = new EventEmitter<SelectionEvent>();

  /** Emitted when delete action is triggered */
  @Output() deleteRows = new EventEmitter<any[]>();

  /** Emitted when export is triggered */
  @Output() exportData = new EventEmitter<ExportEvent>();

  /** Emitted when pagination changes */
  @Output() pageChange = new EventEmitter<any>();

  /** Emitted when filter options need to be refreshed (for dependent filters) */
  @Output() filterOptionsRefresh = new EventEmitter<{ field: string; currentFilters: any; hierarchy: string[] }>();

  @Output() addRecord = new EventEmitter<void>();

  @Output() add = new EventEmitter<any[]>();
  // ========== VIEW CHILDREN ==========

  @ViewChild('dt') table: Table;
  @ViewChildren('filterMultiSelect') multiSelects: QueryList<MultiSelect>;

  // ========== COMPONENT STATE ==========

  /** Currently selected rows */
  selectedRows: any[] = [];

  /** Global search term */
  searchTerm: string = '';

  /** Column filter values (UI state - before OK clicked) */
  checkedFilters: { [field: string]: any[] } = {};

  /** Applied column filter values (after OK clicked) */
  selectedFilters: { [field: string]: any[] } = {};

  /** Filter hierarchy array for dependent filters */
  filterHierarchy: string[] = [];

  /** Select all state for each filter dropdown */
  selectAllFilters: { [field: string]: boolean } = {};

  /** Original data before search filter */
  private originalFilteredData: any[] = [];

  /** Flag to track if search filter is applied */
  private isSearchFilterApplied: boolean = false;

  /** Column settings panel visibility */
  showColumnSettings: boolean = false;

  /** Track which columns have sorting enabled */
  columnSortingEnabled: { [field: string]: boolean } = {};

  /** Track which columns have filtering enabled */
  columnFilteringEnabled: { [field: string]: boolean } = {};

  /** Date pipe for date formatting */
  private datePipe = new DatePipe('en-US');

  /** User preferences store */
  private readonly userPreferencesStore = inject(UserPreferencesStore);
  private readonly authStore = inject(AuthStore);
  private readonly cdr = inject(ChangeDetectorRef);

  private deferUiStateUpdate(updateFn: () => void): void {
    Promise.resolve().then(() => {
      updateFn();
      this.cdr.markForCheck();
    });
  }
  get isHaveAccessToAdd(): boolean {
    if (this.config.preferenceKey === 'groupMembers') {
      const isHaveAccessToAdd = this.authStore.getPermission(Screen.GroupMember, Function.AddGroupMember);
      return isHaveAccessToAdd !== Access.NoAccess;
    }
    if (this.config.preferenceKey === 'studiesLibrary') {
      const isHaveAccessToAdd = this.authStore.getPermission(Screen.Study, Function.AddStudy);
      return isHaveAccessToAdd !== Access.NoAccess;
    }
    const applicantPermission = this.authStore.getPermission(Screen.Restriction, Function.RestrictionAdd);
    return applicantPermission !== Access.NoAccess;
  }

  get isHaveAccessToExport(): boolean {
    // Switch on preferenceKey
    switch (this.config.preferenceKey) {
      case 'GroupMemberAudit':
      case 'GroupMemberStudy':
      case 'preQualifiedApplicants':
        return true;

      case 'RestrictionAudit': {
        const isHaveAccessToExport = this.authStore.getPermission(Screen.Restriction, Function.RestrictionAdd);
        return isHaveAccessToExport !== Access.NoAccess;
      }

      case 'ApplicantAuditBySp':
      case 'applicants': {
        const isHaveAccessToExport = this.authStore.getPermission(Screen.Applicant, Function.ExportApplicant);
        return isHaveAccessToExport !== Access.NoAccess;
      }
      default:
      return false;
    }
  }

  get isHaveAccessToDelete(): boolean {
    if (this.config.preferenceKey === 'applicants') {
      const isHaveAccessToDelete = this.authStore.getPermission(Screen.Applicant, Function.DeleteApplicant);
      return isHaveAccessToDelete !== Access.NoAccess;
    }

    if (this.config.preferenceKey === 'questionLibrary') {
      const isHaveAccessToDelete = this.authStore.getPermission(Screen.QuestionLibrary, Function.DeleteQuestion);
      return isHaveAccessToDelete !== Access.NoAccess;
    }

    if (this.config.preferenceKey === 'studiesLibrary') {
      const isHaveAccessToDelete = this.authStore.getPermission(Screen.Study, Function.DeleteStudy);
      return isHaveAccessToDelete !== Access.NoAccess;
    }

    return false;
  }

  constructor(private confirmationService: ConfirmationService) { }

  ngOnInit(): void {
    this.initializeFilters();
    this.restoreFilterState();
    this.initializeColumnSettings();
    // Initialize filters with all options selected after a short delay
    // to allow parent component to populate filter options
    setTimeout(() => {
      this.initializeFiltersWithAllSelected();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] && !changes['dataSource'].firstChange) {
      // Data source changed, reset original filtered data only if search is not applied
      if (!this.isSearchFilterApplied) {
        this.originalFilteredData = [];
      }

      // Re-initialize filter selections after async data/filterOptions population
      setTimeout(() => {
        this.initializeFiltersWithAllSelected();
      }, 0);
    }

    // When columns change or are updated, initialize filters with all selected
    if (changes['columns']) {
      // Use setTimeout to ensure filter options are populated
      setTimeout(() => {
        this.initializeFiltersWithAllSelected();
      }, 0);
    }
  }

  // ========== INITIALIZATION ==========
  // Initialize column settings (sorting and filtering enabled by default for eligible columns)
  private initializeColumnSettings(): void {
    this.columns.forEach(col => {
      const fieldKey = col.field;
      // Only set if not already restored from preferences
      if (typeof this.columnSortingEnabled[fieldKey] !== 'boolean') {
        this.columnSortingEnabled[fieldKey] = col.sortable !== false;
      }
      if (typeof this.columnFilteringEnabled[fieldKey] !== 'boolean') {
        this.columnFilteringEnabled[fieldKey] = (col.filterable !== false && col.filterType === 'multiselect');
      }
    });
  }

  /**
   * Initialize filter arrays for each filterable column
   */
  private initializeFilters(): void {
    this.columns.forEach(col => {
      if (col.filterable && col.filterType === 'multiselect') {
        const fieldKey = col.filterField || col.field;
        if (!this.checkedFilters[fieldKey]) {
          this.checkedFilters[fieldKey] = [];
        }
        if (!this.selectedFilters[fieldKey]) {
          this.selectedFilters[fieldKey] = [];
        }
        // Default to true - all items selected by default
        if (this.selectAllFilters[fieldKey] === undefined) {
          this.selectAllFilters[fieldKey] = true;
        }
      }
    });
  }

  /**
   * Initialize filters with all options selected by default
   * Call this after populating filter options
   */
  public initializeFiltersWithAllSelected(isFirstNavigation: boolean = true): void {
    this.columns.forEach(col => {
      if (col.filterable && col.filterType === 'multiselect' && col.filterOptions && col.filterOptions.length > 0) {
        const fieldKey = col.filterField || col.field;
        const availableValues = col.filterOptions.map(opt => opt.value);

        // Check current selection state
        const currentSelection = this.selectedFilters[fieldKey] || [];
        const validSelection = currentSelection.filter(val => availableValues.includes(val));

        // Check if this filter is in the hierarchy (user has explicitly applied it)
        const isExplicitlyFiltered = this.filterHierarchy.includes(fieldKey);

        // Determine if we should select all
        const shouldSelectAll =
          // No previous selection at all
          currentSelection.length === 0 ||
          // User hasn't explicitly filtered this column and should select all by default
          (!isExplicitlyFiltered && validSelection.length === 0) ||
          // All previously available options were selected (no explicit partial selection)
          (!isExplicitlyFiltered && validSelection.length === availableValues.length);

        this.deferUiStateUpdate(() => {
          if (shouldSelectAll) {
            // Select all options by default
            this.checkedFilters[fieldKey] = [...availableValues];
            this.selectedFilters[fieldKey] = [...availableValues];
            this.selectAllFilters[fieldKey] = true;
          } else {
            // User has made an explicit partial selection, keep only valid values
            this.checkedFilters[fieldKey] = [...validSelection];
            this.selectedFilters[fieldKey] = [...validSelection];
            this.selectAllFilters[fieldKey] = validSelection.length === availableValues.length;
          }
        });
      }
    });
  }

  /**
   * Restore filter state from UserPreferencesStore
   */
  private restoreFilterState(): void {
    if (!this.config.preferenceKey) return;

    try {
      const storeMethod = `${this.config.preferenceKey}`;
      const savedFilter = this.userPreferencesStore[storeMethod]?.();

      if (savedFilter) {
        // Restore column filters
        if (savedFilter.columnFilters) {
          Object.keys(savedFilter.columnFilters).forEach(field => {
            this.selectedFilters[field] = savedFilter.columnFilters[field] || [];
            this.checkedFilters[field] = [...this.selectedFilters[field]];
          });
        }

        // Restore search term
        if (savedFilter.searchTerm) {
          this.searchTerm = savedFilter.searchTerm;
        }

        // Restore filter hierarchy
        if (savedFilter.filterHierarchy) {
          this.filterHierarchy = [...savedFilter.filterHierarchy];
        }

        // Restore column sorting enabled states
        if (savedFilter.columnSortingEnabled) {
          this.columnSortingEnabled = { ...savedFilter.columnSortingEnabled };
        }

        // Restore column filtering enabled states
        if (savedFilter.columnFilteringEnabled) {
          this.columnFilteringEnabled = { ...savedFilter.columnFilteringEnabled };
        }
      }
    } catch (error) {
      console.warn('Could not restore filter state:', error);
    }
  }

  //Apply filter for a specific column (called when OK button clicked)
  filterColumn(event: Event, column: DynamicColumnConfig): void {
    event.preventDefault();
    event.stopPropagation();

    const fieldKey = column.filterField || column.field;

    // Update selected filters with checked values
    this.selectedFilters[fieldKey] = [...this.checkedFilters[fieldKey]];

    // Update filter hierarchy
    this.updateFilterHierarchy(fieldKey);

    // Close the multiselect dropdown
    this.closeMultiSelect(fieldKey);

    // Reset pagination to first page when filters change
    if (this.table) {
      this.table.first = 0;
    }

    // Emit event to refresh dependent filter options
    if (this.config.supportFilterHierarchy) {
      this.filterOptionsRefresh.emit({
        field: fieldKey,
        currentFilters: { ...this.selectedFilters },
        hierarchy: [...this.filterHierarchy]
      });
    }

    // Apply filter based on lazy/eager mode
    if (this.config.lazy) {
      this.emitLazyLoad();
    } else {
      this.applyClientSideFilter();
    }

    // Emit filter change event (parent component handles persistence)
    this.emitFilterChange();
  }

  /**
   * Clear filter for a specific column (called when Clear button clicked)
   */
  clearColumnFilter(event: Event, column: DynamicColumnConfig): void {
    event.preventDefault();
    event.stopPropagation();

    const fieldKey = column.filterField || column.field;

    // Only clear the checked filters (UI state)
    // Do NOT update selectedFilters or apply any filtering
    this.checkedFilters[fieldKey] = [];
    this.selectAllFilters[fieldKey] = false;
  }

  /**
   * Apply filter by field name (wrapper for HTML template)
   */
  applyFilter(field: string): void {
    const column = this.columns.find(col => (col.filterField || col.field) === field);
    if (column) {
      this.filterColumn(new Event('click'), column);
    }
  }

  /**
   * Clear filter by field name (wrapper for HTML template)
   */
  clearFilter(field: string): void {
    const column = this.columns.find(col => (col.filterField || col.field) === field);
    if (column) {
      this.clearColumnFilter(new Event('click'), column);
    }
  }

  isFilterAllSelected(column: DynamicColumnConfig): boolean {
    const fieldKey = column.filterField || column.field;
    const options = column.filterOptions || [];
    const values = this.checkedFilters[fieldKey] || [];

    if (!options.length || !values.length) {
      return false;
    }

    const optionValues = options.map(option => option.value);
    return optionValues.every(value => values.includes(value));
  }

  isFilterSomeSelected(column: DynamicColumnConfig): boolean {
    const fieldKey = column.filterField || column.field;
    const options = column.filterOptions || [];
    const values = this.checkedFilters[fieldKey] || [];

    if (!options.length || !values.length) {
      return false;
    }

    return !this.isFilterAllSelected(column);
  }

  toggleFilterSelectAll(checked: boolean, column: DynamicColumnConfig, multiSelect?: MultiSelect): void {
    const fieldKey = column.filterField || column.field;
    const allValues = checked ? (column.filterOptions || []).map(option => option.value) : [];

    this.checkedFilters[fieldKey] = [...allValues];
    this.selectAllFilters[fieldKey] = checked;

    if (multiSelect) {
      multiSelect.updateModel([...allValues]);
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle Select All checkbox in filter dropdown
   */
  selectAllFilterOptions(event: Event, column: DynamicColumnConfig): void {
    event.preventDefault();

    if (!column.filterOptions) return;

    const fieldKey = column.filterField || column.field;

    const allSelected = (this.checkedFilters[fieldKey] || []).length === column.filterOptions.length;

    if (allSelected) {
      this.checkedFilters[fieldKey] = [];
    } else {
      this.checkedFilters[fieldKey] = column.filterOptions.map(opt => opt.value);
    }
  }

  /**
   * Check if all filter options are selected
   */
  isAllFilterOptionsSelected(column: DynamicColumnConfig): boolean {
    if (!column.filterOptions) return false;

    const fieldKey = column.filterField || column.field;
    return (this.checkedFilters[fieldKey] || []).length === column.filterOptions.length;
  }

  /**
   * Update filter hierarchy when a filter is applied
   */
  private updateFilterHierarchy(field: string): void {
    if (!this.config.supportFilterHierarchy) return;

    const index = this.filterHierarchy.indexOf(field);
    if (index > -1) {
      this.filterHierarchy.splice(index, 1);
    }

    const column = this.columns.find(col => (col.filterField || col.field) === field);
    const allOptionsCount = column?.filterOptions?.length || 0;
    const selectedCount = this.selectedFilters[field]?.length || 0;
    if (selectedCount > 0 && selectedCount < allOptionsCount) {
      this.filterHierarchy.unshift(field);
    }
  }

  /**
   * Remove field from filter hierarchy
   */
  private removeFromFilterHierarchy(field: string): void {
    const index = this.filterHierarchy.indexOf(field);
    if (index > -1) {
      this.filterHierarchy.splice(index, 1);
    }
  }

  /**
   * Close multiselect dropdown for a specific field
   */
  private closeMultiSelect(field: string): void {
    this.multiSelects?.forEach(ms => {
      if (ms.el?.nativeElement?.dataset?.field === field) {
        ms.overlayVisible = false;
      }
    });
  }

  /**
   * Apply filters on client side (for non-lazy tables)
   */
  private applyClientSideFilter(): void {
    if (!this.table) return;

    let filteredData = [...this.dataSource];

    // Apply column filters
    Object.keys(this.selectedFilters).forEach(field => {
      const filterValues = this.selectedFilters[field];
      const column = this.columns.find(col => (col.filterField || col.field) === field);
      const allOptionsCount = column?.filterOptions?.length || 0;
      if (filterValues && filterValues.length > 0 && filterValues.length < allOptionsCount) {
        filteredData = filteredData.filter(row => {
          const cellValue = this.getCellValue(row, field);
          return filterValues.includes(cellValue);
        });
      }
    });

    // Update table value
    this.table.value = filteredData;

    // Store for search reset
    if (!this.isSearchFilterApplied) {
      this.originalFilteredData = [...filteredData];
    }

    // Reset pagination to first page after applying filter
    if (this.table) {
      this.table.first = 0;
    }
  }

  /**
   * Emit lazy load event for server-side filtering
   */
  private emitLazyLoad(): void {
    const event: LazyLoadEvent = {
      first: this.table?.first || 0,
      rows: this.config.rows || 50,
      sortField: this.table?.sortField,
      sortOrder: this.table?.sortOrder,
      filters: this.buildPrimeNGFilters()
    };

    this.lazyLoad.emit(event);
  }

  /**
   * Build PrimeNG-compatible filters object
   */
  private buildPrimeNGFilters(): { [field: string]: any } {
    const filters: { [field: string]: any } = {};

    Object.keys(this.selectedFilters).forEach(field => {
      const values = this.selectedFilters[field];

      // Find the column to check if all options are selected
      const column = this.columns.find(col => (col.filterField || col.field) === field);
      const allOptionsCount = column?.filterOptions?.length || 0;

      // Only add filter if values exist and not all options are selected
      if (values && values.length > 0 && values.length < allOptionsCount) {
        filters[field] = { value: values, matchMode: 'in' };
      }
    });

    return filters;
  }

  /**
   * Emit filter change event
   */
  private emitFilterChange(): void {
    const filterState: DynamicTableFilter = {
      columnFilters: { ...this.selectedFilters },
      searchTerm: this.searchTerm,
      filterHierarchy: [...this.filterHierarchy],
      page: this.table?.first || 0,
      rows: this.config.rows,
      sortField: this.table?.sortField,
      sortOrder: this.table?.sortOrder,
      columnSortingEnabled: { ...this.columnSortingEnabled },
      columnFilteringEnabled: { ...this.columnFilteringEnabled }
    };

    this.filterChange.emit(filterState);
  }

  // ========== GLOBAL SEARCH ==========

  /**
   * Handle global search input
   */
  onGlobalSearch(event: any): void {
    const searchValue = event.target.value;
    this.applyGlobalSearch(searchValue, true);
  }

  /**
   * Apply global search programmatically.
   */
  public applySearchTerm(searchValue: string, emitChange: boolean = false): void {
    this.applyGlobalSearch(searchValue, emitChange);
  }

  private applyGlobalSearch(searchValue: string, emitChange: boolean): void {
    this.searchTerm = searchValue;
    this.isSearchFilterApplied = true;

    // Reset pagination to first page when search changes
    if (this.table) {
      this.table.first = 0;
    }

    if (!searchValue || searchValue.trim() === '') {
      // Reset to original filtered data
      if (this.originalFilteredData.length > 0) {
        if (this.table) {
          this.table.value = [...this.originalFilteredData];
        }
        this.originalFilteredData = [];
      }
      this.isSearchFilterApplied = false;
      this.searchTerm = '';
      if (emitChange) {
        this.emitFilterChange();
      }
      return;
    }
    // Store original data if not already stored
    if (this.originalFilteredData.length === 0) {
      // Use table.value if it exists and has data, otherwise use dataSource
      const currentData = (this.table?.value && this.table.value.length > 0)
        ? this.table.value
        : this.dataSource;
      if (currentData && currentData.length > 0) {
        this.originalFilteredData = [...currentData];
      } else {
        // No data to search
        return;
      }
    }
    const searchTerm = searchValue.toLowerCase().trim();
    // Filter across all columns
    const filtered = this.originalFilteredData.filter(item => {
      return this.columns.some(col => {
        const searchField = col.filterField || col.field;
        const cellValue = this.getCellValue(item, searchField);
        // Handle different data types
        if (col.dataType === 'date' && cellValue) {
          const dateDisplay = this.datePipe.transform(cellValue, 'MM/dd/yyyy') || '';
          return dateDisplay.toLowerCase().includes(searchTerm);
        } else if (cellValue !== null && cellValue !== undefined) {
          return String(cellValue).toLowerCase().includes(searchTerm);
        }
        return false;
      });
    });
    // Update table value
    if (this.table) {
      this.table.value = filtered;
    }
    if (emitChange) {
      this.emitFilterChange();
    }
  }

  // ========== ROW SELECTION ==========

  /**
   * Handle row select event
   */
  onRowSelect(event: any): void {
    this.emitSelectionEvent('select');
  }

  /**
   * Handle row unselect event
   */
  onRowUnselect(event: any): void {
    this.emitSelectionEvent('unselect');
  }

  /**
   * Handle header checkbox toggle
   */
  onHeaderCheckboxToggle(event: any): void {
    const eventType = event.checked ? 'selectAll' : 'unselectAll';
    this.emitSelectionEvent(eventType);
  }

  isRowSelected(row: any): boolean {
    const dataKey = this.config.dataKey || 'id';
    return this.selectedRows.some(selected => selected?.[dataKey] === row?.[dataKey]);
  }

  isAllRowsSelected(): boolean {
    const selectableRows = this.getSelectableVisibleRows();
    return selectableRows.length > 0 && selectableRows.every(row => this.isRowSelected(row));
  }

  isSomeRowsSelected(): boolean {
    const selectableRows = this.getSelectableVisibleRows();
    if (!selectableRows.length) {
      return false;
    }

    const selectedCount = selectableRows.filter(row => this.isRowSelected(row)).length;
    return selectedCount > 0 && selectedCount < selectableRows.length;
  }

  toggleRowSelection(row: any, checked: boolean): void {
    if (this.isRowSelectionDisabled(row)) {
      return;
    }

    const dataKey = this.config.dataKey || 'id';

    if (checked) {
      if (!this.isRowSelected(row)) {
        this.selectedRows = [...this.selectedRows, row];
        this.emitSelectionEvent('select');
      }
    } else {
      this.selectedRows = this.selectedRows.filter(selected => selected?.[dataKey] !== row?.[dataKey]);
      this.emitSelectionEvent('unselect');
    }
  }

  toggleAllRows(checked: boolean): void {
    const selectableRows = this.getSelectableVisibleRows();

    if (checked) {
      const existingRows = [...this.selectedRows];
      const dataKey = this.config.dataKey || 'id';
      const existingKeys = new Set(existingRows.map(row => row?.[dataKey]));
      const rowsToAdd = selectableRows.filter(row => !existingKeys.has(row?.[dataKey]));
      this.selectedRows = [...existingRows, ...rowsToAdd];
      this.emitSelectionEvent('selectAll');
    } else {
      const dataKey = this.config.dataKey || 'id';
      const selectableKeys = new Set(selectableRows.map(row => row?.[dataKey]));
      this.selectedRows = this.selectedRows.filter(row => !selectableKeys.has(row?.[dataKey]));
      this.emitSelectionEvent('unselectAll');
    }
  }

  /**
   * Emit selection change event
   */
  private emitSelectionEvent(type: 'select' | 'unselect' | 'selectAll' | 'unselectAll'): void {
    const selectionEvent: SelectionEvent = {
      selectedRows: [...this.selectedRows],
      type: type
    };

    this.rowsSelected.emit(selectionEvent);
  }

  /**
   * Check if row should be disabled for selection
   */
  isRowSelectionDisabled(row: any): boolean {
    if (typeof this.disableSelection === 'function') {
      return this.disableSelection(row);
    }
    return false;
  }

  /**
   * Check if header checkbox should be disabled
   */
  isHeaderCheckboxDisabled(): boolean {
    if (!this.config.selectable) return true;

    const selectableRows = this.getSelectableVisibleRows();
    return selectableRows.length === 0;
  }

  private getSelectableVisibleRows(): any[] {
    const visibleRows = this.table?.value || this.dataSource || [];
    return visibleRows.filter(row => !this.isRowSelectionDisabled(row));
  }

  // ========== PAGINATION ==========

  /**
   * Handle page change event
   */
  onPageChange(event: any): void {
    if (this.config.lazy) {
      const lazyEvent: LazyLoadEvent = {
        first: event.first,
        rows: event.rows,
        sortField: this.table?.sortField,
        sortOrder: this.table?.sortOrder,
        filters: this.buildPrimeNGFilters()
      };

      this.lazyLoad.emit(lazyEvent);
    }

    // Emit page change for parent components that need it
    this.pageChange.emit(event);
  }

  // ========== DELETE FUNCTIONALITY ==========

  /**
   * Handle delete button click
   */
  onDeleteClick(): void {
    if (this.selectedRows.length === 0) return;
    if (this.config.preferenceKey === 'questionLibrary') {
      this.deleteRows.emit([...this.selectedRows]);
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to delete?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deleteRows.emit([...this.selectedRows]);
      }
    });
  }

  /**
   * Check if delete button should be disabled
   */
  isDeleteDisabled(): boolean {
    return this.selectedRows.length === 0 ||
      this.selectedRows.every(row => this.isRowSelectionDisabled(row));
  }

  // ========== EXPORT FUNCTIONALITY ==========

  /**
   * Export data (filtered if filters are applied, otherwise all data)
   */
  export(): void {
    // Check if any filters are applied (excluding filters where all options are selected)
    const hasActiveFilters = Object.keys(this.selectedFilters).some(field => {
      const filterValues = this.selectedFilters[field];
      if (!filterValues || filterValues.length === 0) return false;

      // Find the column to check if all options are selected
      const column = this.columns.find(col => (col.filterField || col.field) === field);
      const allOptionsCount = column?.filterOptions?.length || 0;

      // Only consider it an active filter if not all options are selected
      return filterValues.length > 0 && filterValues.length < allOptionsCount;
    });
    const hasSearchTerm = this.searchTerm && this.searchTerm.trim() !== '';

    const exportEvent: ExportEvent = {
      type: hasActiveFilters || hasSearchTerm ? 'filtered' : 'all',
      filterState: {
        columnFilters: { ...this.selectedFilters },
        searchTerm: this.searchTerm,
        filterHierarchy: [...this.filterHierarchy]
      }
    };

    this.exportData.emit(exportEvent);
  }

  // ========== UTILITY METHODS ==========

  /**
   * Get cell value from row data
   */
  private getCellValue(row: any, field: string): any {
    if (!row) return null;

    // Support nested fields with dot notation
    const fields = field.split('.');
    let value = row;

    for (const f of fields) {
      if (value === null || value === undefined) return null;
      value = value[f];
    }

    return value;
  }

  /**
   * Get display value for a cell
   */
  getDisplayValue(row: any, column: DynamicColumnConfig): string {
    const value = this.getCellValue(row, column.field);

    if (value === null || value === undefined) {
      return column.dataType === 'number' ? '0' : '';
    }

    // Format based on data type
    switch (column.dataType) {
      case 'date':
        return this.datePipe.transform(value, 'dd/MM/yyyy') || '';
      case 'number':
        return String(value);
      case 'boolean':
        return value ? 'Yes' : 'No';
      default:
        return String(value);
    }
  }

  /**
   * Check if field has custom template
   */
  hasCustomTemplate(field: string): boolean {
    const column = this.columns.find(col => col.field === field);
    return !!column?.customTemplate && !!this.cellTemplates[column.customTemplate];
  }

  /**
   * Get custom template for field
   */
  getCellTemplate(field: string): TemplateRef<any> | null {
    const column = this.columns.find(col => col.field === field);
    if (column?.customTemplate) {
      return this.cellTemplates[column.customTemplate] || null;
    }
    return null;
  }

  /**
   * Get sort field for column
   */
  getSortField(column: DynamicColumnConfig): string {
    return column.sortField || column.field;
  }

  /**
   * Toggle select all for a filter dropdown
   */
  toggleSelectAll(field: string, checked: boolean): void {
    const column = this.columns.find(col => (col.filterField || col.field) === field);
    if (column && column.filterOptions) {
      if (checked) {
        this.checkedFilters[field] = column.filterOptions.map(opt => opt.value);
        this.selectAllFilters[field] = true;
      } else {
        this.checkedFilters[field] = [];
        this.selectAllFilters[field] = false;
      }
    }
  }

  /**
   * Public method to update filter options for a column
   * Call this when filter options change dynamically
   */
  updateColumnFilterOptions(field: string, options: FilterOption[]): void {
    const column = this.columns.find(col => (col.filterField || col.field) === field);
    if (column) {
      column.filterOptions = options;

      const availableValues = options.map(opt => opt.value);
      const currentSelection = this.selectedFilters[field] || [];
      const validSelection = currentSelection.filter(val => availableValues.includes(val));

      // Check if this filter is in the hierarchy (user has explicitly applied it)
      const isExplicitlyFiltered = this.filterHierarchy.includes(field);

      // Determine if we should select all
      const shouldSelectAll =
        // No previous selection at all
        currentSelection.length === 0 ||
        // User hasn't explicitly filtered this column and should select all by default
        (!isExplicitlyFiltered && validSelection.length === 0) ||
        // All previously available options were selected (no explicit partial selection)
        (!isExplicitlyFiltered && validSelection.length === availableValues.length);

      this.deferUiStateUpdate(() => {
        if (shouldSelectAll) {
          this.checkedFilters[field] = [...availableValues];
          this.selectedFilters[field] = [...availableValues];
          this.selectAllFilters[field] = true;
        } else {
          this.checkedFilters[field] = [...validSelection];
          this.selectedFilters[field] = [...validSelection];
          this.selectAllFilters[field] = validSelection.length === availableValues.length;
        }
      });
    }
  }

  // ========== COLUMN SETTINGS ==========

  /**
   * Toggle column settings panel visibility
   */
  toggleColumnSettings(): void {
    this.showColumnSettings = !this.showColumnSettings;
  }

  /**
   * Close column settings panel
   */
  closeColumnSettings(): void {
    this.showColumnSettings = false;
  }

  /**
   * Toggle sorting for a specific column
   */
  toggleColumnSorting(field: string, enabled: boolean): void {
    this.columnSortingEnabled[field] = enabled;
    // If sorting is disabled and the table is currently sorted by this column, clear the sort
    if (!enabled && this.table?.sortField === field) {
      this.table.sortField = null;
      this.table.sortOrder = 1;
    }
  }

  /**
   * Toggle filtering for a specific column
   */
  toggleColumnFiltering(field: string, enabled: boolean): void {
    this.columnFilteringEnabled[field] = enabled;
    // If filtering is disabled, clear this column's filter
    if (!enabled) {
      const filterField = this.columns.find(col => col.field === field)?.filterField || field;
      this.checkedFilters[filterField] = [];
      this.selectedFilters[filterField] = [];
      this.removeFromFilterHierarchy(filterField);

      // Reset pagination to first page when filters change
      if (this.table) {
        this.table.first = 0;
      }

      // Reapply filters
      if (this.config.lazy) {
        this.emitLazyLoad();
      } else {
        this.applyClientSideFilter();
      }
      this.emitFilterChange();
    }
  }

  /**
   * Check if a column should show sorting controls
   */
  isColumnSortingEnabled(column: DynamicColumnConfig): boolean {
    return column.sortable !== false && this.columnSortingEnabled[column.field] === true;
  }

  /**
   * Check if a column should show filtering controls
   */
  isColumnFilteringEnabled(column: DynamicColumnConfig): boolean {
    const fieldKey = column.filterField || column.field;
    return column.filterable !== false && this.columnFilteringEnabled[column.field] === true;
  }

  /**
   * Get list of columns for settings panel
   */
  getColumnsForSettings(): DynamicColumnConfig[] {
    return this.columns.filter(col => col.sortable !== false || col.filterable !== false);
  }

  /**
   * Determine if pagination should be shown
   * Only show pagination if records exceed 10
   */
  shouldShowPagination(): boolean {
    // If showPagination is explicitly set to false, don't show pagination
    if (this.config.showPagination === false) {
      return false;
    }

    const visibleCount = this.table?.value?.length;
    const recordCount = this.config.lazy
      ? this.totalRecords
      : (visibleCount ?? this.dataSource?.length ?? 0);

    return recordCount > 10;
  }

  getCellDisplayWithTooltip(rowData: any, col: any): { display: string, tooltip: string | null } {
    const value = rowData[col.field];
    if (col.wrap) {
      return { display: value, tooltip: null };
    }
    if (typeof value === 'string' && value.length > 25) {
      return {
        display: value.slice(0, 25) + '...',
        tooltip: value
      };
    }
    return {
      display: value,
      tooltip: null
    };
  }
}
